'use client';

import { useMemo, useState } from 'react';
import { Loader } from '@mantine/core';
import Link from 'next/link';

import { useGetPlanList } from '@/components/modules/Landing/api';
import { BILLING_INTERVAL, IPlan, PLAN_TYPE } from '@/types';
import PageLink from '@/utils/PageLink';
import { getPlanModelAccess } from '@/utils/plan-model-access';
import { getPlanPrice } from '@/utils/pricing-helpers';

/* ---------- helpers for formatting ---------- */

const formatAbbrevNumber = (value: number) => {
  if (!value) return '0';
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${Number.isInteger(millions) ? millions : millions.toFixed(1)}M`;
  }
  if (value >= 1_000) {
    const thousands = value / 1_000;
    return `${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}K`;
  }
  return value.toString();
};

const formatWithCommas = (value: number) => value.toLocaleString();

const getFeature = (plan: IPlan, name: string) => plan.features?.find((f) => f.name === name);

/* ---------- build display feature lines from DB values ---------- */

const buildDisplayFeatures = (plan: IPlan): string[] => {
  const lines: string[] = [];

  // Chatbots – show regular chatbots count (not expert chatbots)
  const chatBot = getFeature(plan, 'chatBot');
  if (chatBot) {
    let value: string;
    if (chatBot.isUnlimited && chatBot.quantity > 0 && plan.type === PLAN_TYPE.ENTERPRISE) {
      value = `${chatBot.quantity}+ (or Unlimited)`;
    } else if (chatBot.isUnlimited) {
      value = 'Unlimited';
    } else {
      value = chatBot.quantity.toString();
    }
    lines.push(`Chatbots: ${value}`);
  }

  // Message Credits
  const msg = getFeature(plan, 'messageCredit');
  if (msg) {
    let value: string;
    if (msg.isUnlimited && msg.quantity > 0 && plan.type === PLAN_TYPE.ENTERPRISE) {
      value = `${formatAbbrevNumber(msg.quantity)}+ / Unlimited`;
      lines.push(`Message Credits: ${value}`);
    } else {
      if (msg.isUnlimited) {
        value = 'Unlimited';
      } else {
        value = formatWithCommas(msg.quantity);
      }
      lines.push(`Message Credits: ${value}/month`);
    }
  }

  // Team Seats
  const seats = getFeature(plan, 'teamSeats');
  if (seats) {
    const value = seats.isUnlimited ? 'Unlimited' : seats.quantity.toString();
    lines.push(`Team Seats: ${value}`);
  }

  // Knowledge Base
  const kb = getFeature(plan, 'knowledgeBase');
  if (kb) {
    let value: string;
    if (kb.isUnlimited && plan.type === PLAN_TYPE.ENTERPRISE) {
      value = 'Custom';
    } else if (kb.isUnlimited) {
      value = `${formatAbbrevNumber(kb.quantity)}+ characters`;
    } else {
      value = `${formatAbbrevNumber(kb.quantity)} characters`;
    }
    lines.push(`Knowledge Base: ${value}`);
  }

  lines.push(getPlanModelAccess(plan));

  // Privacy
  const privateChatbots = getFeature(plan, 'privateChatbots');
  const hasPrivate =
    !!privateChatbots && (privateChatbots.isUnlimited || privateChatbots.quantity > 0);
  lines.push(`Privacy: ${hasPrivate ? 'Public / Private' : 'Public'}`);

  // URL Crawl Limit
  const crawls = getFeature(plan, 'urlCrawls');
  if (crawls) {
    let value: string;
    if (crawls.isUnlimited && plan.type === PLAN_TYPE.ENTERPRISE) {
      value = 'Custom / Unlimited';
    } else if (crawls.isUnlimited) {
      value = 'Unlimited';
    } else {
      value = `${formatAbbrevNumber(crawls.quantity)} Pages/month`;
    }
    lines.push(`URL Crawl Limit: ${value}`);
  }

  // Branding
  let branding = '';
  switch (plan.type) {
    case PLAN_TYPE.FREE:
    case PLAN_TYPE.BASIC:
      branding = 'Yes (with branding)';
      break;
    case PLAN_TYPE.PREMIUM:
      branding = 'Can be removed';
      break;
    case PLAN_TYPE.ENTERPRISE:
      branding = 'Full White-Label';
      break;
  }
  if (branding) lines.push(`Branding: ${branding}`);

  // Support
  let support = '';
  switch (plan.type) {
    case PLAN_TYPE.FREE:
      support = 'Community';
      break;
    case PLAN_TYPE.BASIC:
      support = 'Email + Community';
      break;
    case PLAN_TYPE.PREMIUM:
      support = 'Priority Email / Chat';
      break;
    case PLAN_TYPE.ENTERPRISE:
      support = 'Dedicated Manager + SLA';
      break;
  }
  if (support) lines.push(`Support: ${support}`);

  // Key Features
  let keyFeatures = '';
  switch (plan.type) {
    case PLAN_TYPE.FREE:
      keyFeatures = 'Train your AI in minutes';
      break;
    case PLAN_TYPE.BASIC:
      keyFeatures = 'Core for Solo Use';
      break;
    case PLAN_TYPE.PREMIUM:
      keyFeatures = 'Collaboration, High Limits';
      break;
    case PLAN_TYPE.ENTERPRISE:
      keyFeatures = 'Scale, Security, Customization';
      break;
  }
  if (keyFeatures) lines.push(`Key Features: ${keyFeatures}`);

  return lines;
};

/* ---------- get variant from plan type ---------- */

const getVariant = (planType: string): 'starter' | 'pro' | 'business' | 'enterprise' => {
  switch (planType) {
    case PLAN_TYPE.FREE:
      return 'starter';
    case PLAN_TYPE.BASIC:
      return 'pro';
    case PLAN_TYPE.PREMIUM:
      return 'business';
    case PLAN_TYPE.ENTERPRISE:
      return 'enterprise';
    default:
      return 'starter';
  }
};

/* ---------- get display name from plan type ---------- */

const getDisplayName = (plan: IPlan): string => {
  // FIXED: Use actual database plan name instead of hardcoded mappings
  // Priority: pageData.name (custom) > plan.name (database)
  return plan.pageData?.name || plan.name;
};

/* ---------- get description from plan type ---------- */

const getDescription = (planType: string): string => {
  switch (planType) {
    case PLAN_TYPE.FREE:
      return 'For individuals and entrepreneurs';
    case PLAN_TYPE.BASIC:
      return 'For solo professionals';
    case PLAN_TYPE.PREMIUM:
      return 'For small businesses/teams';
    case PLAN_TYPE.ENTERPRISE:
      return 'For mid-to-large enterprises';
    default:
      return '';
  }
};

/* ---------- get badge from plan type ---------- */

const getBadge = (planType: string): string | null => {
  switch (planType) {
    case PLAN_TYPE.FREE:
      return '14-Day Free Trial';
    case PLAN_TYPE.BASIC:
      return 'Popular';
    case PLAN_TYPE.PREMIUM:
      return 'Best Value';
    default:
      return null;
  }
};

function Check({ variant }: { variant: string }) {
  const color = variant === 'enterprise' ? '#000000' : '#004790';
  return (
    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center">
      <svg aria-hidden="true" viewBox="0 0 16 16" className="h-4 w-4" fill="none">
        <path
          d="M3.75 8.5 6.5 11.25 12.25 5.5"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function formatPrice(value: number) {
  return `$${value.toLocaleString('en-US')}`;
}

const BrandPricing: React.FC<{ initialPlans?: IPlan[]; initialPlansFetchedAt?: number }> = ({
  initialPlans,
  initialPlansFetchedAt,
}) => {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const {
    data: planList,
    isLoading,
    isError,
  } = useGetPlanList(initialPlans, initialPlansFetchedAt);

  const suffix = billing === 'monthly' ? '/mo' : '/yr';

  // Filter and prepare plans for display
  const displayPlans = useMemo(() => {
    if (!planList) return [];

    // Filter out add-ons and get only subscription plans
    const subscriptionPlans = planList.filter((plan) => {
      const planType = String(plan.type);

      return (
        planType !== PLAN_TYPE.ADD_ON && plan.internalId !== 'shopify-plan' && planType !== 'PAID'
      );
    });

    // Sort by plan hierarchy: FREE -> BASIC -> PREMIUM -> ENTERPRISE
    const planOrder: Record<string, number> = {
      [PLAN_TYPE.FREE]: 0,
      [PLAN_TYPE.BASIC]: 1,
      [PLAN_TYPE.PREMIUM]: 2,
      [PLAN_TYPE.ENTERPRISE]: 3,
    };

    return subscriptionPlans
      .sort((a, b) => (planOrder[a.type] ?? 99) - (planOrder[b.type] ?? 99))
      .map((plan) => {
        const variant = getVariant(plan.type);
        const billingCycle = billing === 'monthly' ? BILLING_INTERVAL.MONTH : BILLING_INTERVAL.YEAR;

        const isEnterprise = plan.type === PLAN_TYPE.ENTERPRISE;

        // Use helper function for backward-compatible pricing
        // NEW plans: Uses annualPrice/annualDiscountedPrice from DB
        // OLD plans: Falls back to monthly * 10
        const currentPrice = getPlanPrice(plan, billingCycle, false); // Display price (with discount)
        const originalPrice = getPlanPrice(plan, billingCycle, true); // Original price (before discount)

        const displayPrice = isEnterprise ? 'Custom' : formatPrice(currentPrice);
        // Don't show fake original prices for Starter plan - we have real pricing with trial now
        const isFreePlan = plan.type === PLAN_TYPE.FREE;
        const displayOriginal = isFreePlan
          ? null // No strikethrough for Starter - just show $20 with trial tagline
          : !isEnterprise && originalPrice > currentPrice
            ? formatPrice(originalPrice)
            : null;

        return {
          id: plan.id,
          name: getDisplayName(plan),
          description: getDescription(plan.type),
          variant,
          badge: getBadge(plan.type),
          displayPrice,
          displayOriginal,
          monthlyPrice: plan.discountedPrice || plan.price || 0, // Keep for compatibility
          originalMonthlyPrice: plan.price || 0, // Keep for compatibility
          isEnterprise,
          items: buildDisplayFeatures(plan),
          planType: plan.type,
        };
      });
  }, [planList, billing]);

  // Card styles based on variant
  const getCardStyles = (variant: string) => {
    switch (variant) {
      case 'starter':
        return 'border border-[#e5e7eb] bg-white';
      case 'pro':
        return 'border border-[#c5dcfe] bg-white';
      case 'business':
        return 'border-2 border-[#004790] bg-white shadow-[0px_12px_32px_rgba(0,71,144,0.18)] scale-[1.02]';
      case 'enterprise':
        return 'border border-black bg-white';
      default:
        return 'border border-gray-200 bg-white';
    }
  };

  // Badge styles based on variant
  const getBadgeStyles = (variant: string) => {
    switch (variant) {
      case 'starter':
        return 'bg-[#10b981] text-white';
      case 'pro':
        return 'bg-[#d3a641] text-black';
      case 'business':
        return 'bg-[#c5dcfe] text-[#004790]';
      default:
        return '';
    }
  };

  // Button styles based on variant
  const getButtonStyles = (variant: string) => {
    switch (variant) {
      case 'starter':
        return 'border-2 border-[#004790] text-[#004790] bg-white hover:bg-[#c5dcfe] transition-colors';
      case 'pro':
        return 'bg-[#004790] text-white hover:bg-[#003b78] transition-colors';
      case 'business':
        return 'bg-[#004790] text-white hover:bg-[#003b78] transition-colors font-semibold';
      case 'enterprise':
        return 'border-2 border-black text-black bg-white hover:bg-[#f3f4f6] transition-colors';
      default:
        return 'bg-[#004790] text-white';
    }
  };

  return (
    <section id="pricing-brand" aria-label="Pricing" className="bg-white py-16 sm:py-20">
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-black sm:text-4xl lg:text-5xl">
            Simple pricing. <span className="text-[#004790]">Built to grow with you.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-[#475569] sm:text-lg">
            Try Chat.co free for 14 days. Choose a paid plan to continue after your trial.
          </p>

          {/* Toggle */}
          <div className="mt-6 flex items-center justify-center">
            <div className="inline-flex rounded-full bg-[#f3f4f6] p-1">
              <button
                type="button"
                aria-pressed={billing === 'monthly'}
                onClick={() => setBilling('monthly')}
                className={`rounded-full px-6 py-2.5 text-sm font-medium transition-all ${
                  billing === 'monthly'
                    ? 'bg-[#004790] text-white shadow-sm'
                    : 'text-black hover:text-[#004790]'
                }`}>
                Monthly
              </button>
              <button
                type="button"
                aria-pressed={billing === 'annual'}
                onClick={() => setBilling('annual')}
                className={`rounded-full px-6 py-2.5 text-sm font-medium transition-all ${
                  billing === 'annual'
                    ? 'bg-[#004790] text-white shadow-sm'
                    : 'text-black hover:text-[#004790]'
                }`}>
                Annual
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="mt-16 flex h-full w-full items-center justify-center">
            <Loader color="#004790" />
          </div>
        )}

        {/* Error State */}
        {!isLoading && isError && displayPlans.length === 0 && (
          <div className="mt-16 flex h-full w-full flex-col items-center justify-center gap-4">
            <p className="text-center text-gray-600">
              Unable to load pricing plans. Please try again later.
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && displayPlans.length === 0 && (
          <div className="mt-16 flex h-full w-full flex-col items-center justify-center gap-4">
            <p className="text-center text-gray-600">No pricing plans available at the moment.</p>
          </div>
        )}

        {/* Pricing Cards */}
        {displayPlans.length > 0 && (
          <div className="mt-12 grid grid-cols-1 gap-6 sm:mt-16 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {displayPlans.map((t) => (
              <article
                key={t.id}
                className={`relative flex h-full flex-col rounded-2xl px-6 pb-6 pt-7 sm:px-7 sm:pb-7 sm:pt-8 ${getCardStyles(t.variant)}`}>
                {/* Badge */}
                {t.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${getBadgeStyles(t.variant)}`}>
                      <span>⭐</span> {t.badge}
                    </span>
                  </div>
                )}

                {/* Plan Name */}
                <div className={t.badge ? 'mt-2' : ''}>
                  <h3 className="text-lg font-semibold text-black">{t.name}</h3>
                  <p className="mt-1 text-sm text-gray-600">{t.description}</p>

                  {/* Price */}
                  <div className="mt-3 flex items-baseline gap-2">
                    {t.displayOriginal && (
                      <span className="text-base text-gray-400 line-through">
                        {t.displayOriginal}
                      </span>
                    )}
                    <span className="text-3xl font-bold text-black">{t.displayPrice}</span>
                    {t.displayPrice !== 'Custom' && (
                      <span className="text-base text-gray-500">{suffix}</span>
                    )}
                  </div>
                  {/* Trial tagline - show for Starter plan */}
                  {t.variant === 'starter' && (
                    <p className="mt-1 text-sm font-medium text-[#004790]">
                      14-day free trial; paid subscription required afterward.
                    </p>
                  )}
                  {/* Launch price - show for Pro and Business on monthly */}
                  {billing === 'monthly' && (t.variant === 'pro' || t.variant === 'business') && (
                    <p className="mt-1 text-xs text-[#b88a1e]">Launch price</p>
                  )}
                  {/* 2 months free - show under annual for Pro and Business */}
                  {billing === 'annual' && (t.variant === 'pro' || t.variant === 'business') && (
                    <p className="mt-1 text-xs text-[#b88a1e]">
                      2 months free · Pay for 10 months only
                    </p>
                  )}
                </div>

                {/* Divider */}
                <div className="my-6 h-px w-full bg-[#f3f4f6]" />

                {/* Features */}
                <ul className="flex-1 space-y-3">
                  {t.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check variant={t.variant} />
                      <span className="text-sm text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <div className="mt-8">
                  <Link
                    href={t.variant === 'enterprise' ? PageLink.contactUs : PageLink.login}
                    className={`inline-flex h-12 w-full items-center justify-center rounded-full text-sm ${getButtonStyles(t.variant)}`}>
                    {t.variant === 'enterprise'
                      ? 'Contact Sales'
                      : t.variant === 'starter'
                        ? 'Start 14-Day Trial'
                        : 'Get Started'}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Social Proof Strip */}
        {/* <div className="mt-16 rounded-2xl bg-[#f3f4f6] px-6 py-8 text-center">
          <p className="text-sm font-medium text-black">
            Trusted by 500+ businesses worldwide
          </p>
        </div> */}
      </div>
    </section>
  );
};

export default BrandPricing;
